<script setup lang="ts">
  import { computed } from 'vue'
  import type { TextImage } from '@/types/TextImage'
  import { useImage } from '@/composables/useImage'

  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Typography from '@/components/common/Typography.vue'

  const props = defineProps<{
    element: TextImage
  }>()

  const { src, altText, copyright, caption } = useImage({
    imageId: props.element.image?.destinationContentId ?? '',
    svgId: props.element.svg?.destinationContentId ?? '',
  })

  const noWrapperClass = computed(() => !src.value && 'wrapper--no-image')
</script>

<template>
  <section
    class="text-image-element"
    data-track-context="tekst-og-bilde-element"
    :class="element?.colorPair"
  >
    <div :class="['wrapper', element?.imageAlign, noWrapperClass]">
      <div class="text">
        <Typography
          v-if="element?.title"
          variant="h2"
        >
          {{ element?.title }}
        </Typography>
        <RichTextRenderer :rich-text="element?.richText ?? ''" />
      </div>

      <ImageWithCaption
        v-if="src"
        :src="src"
        :alt-text="altText"
        :caption="caption"
        :copyright="copyright"
        :object-fit="'cover'"
        class="image"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  $padding-mobile: 64px 24px;
  $padding-desktop: 120px;

  .app-layout {
    width: 100%;
  }

  .text-image-element {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: $padding-desktop 32px;
    background-color: var(--color-pair-background-color);
    color: var(--color-pair-color);

    @include vgs-media-mobile {
      min-height: auto;
      padding: $padding-mobile;
    }

    .wrapper {
      $max-width-vertical: 794px;
      $max-width-horizontal: 1200px;

      gap: 96px;
      display: flex;
      align-items: center;
      max-width: var(--app-max-width);

      &--no-image {
        max-width: $vgs-max-width-content;
      }

      @include vgs-media-mobile-and-tablet {
        padding: 0;
      }

      @include vgs-media-mobile {
        flex-direction: column;
        gap: 64px;
      }

      .cms-p {
        @include body;
      }

      .text {
        flex: 1;
        align-self: center;
        line-height: 32px;
        width: 100%;
        word-break: break-word;
      }

      .image {
        flex: 1;
        height: 100%;
      }

      @layer default {
        &.image-right,
        &.image-left {
          gap: 112px;
          max-width: $max-width-horizontal;
        }

        &.image-left {
          flex-direction: row-reverse;
        }

        &.image-right {
          flex-direction: row;
        }
      }
    }
  }
</style>
